/** Plan prices for the person looking (signed-in business, visitor, or a picked country). */
import useAuthStore from "@/stores/authStore";
import { COUNTRIES, useCurrencyStore } from "@/lib/currency";
import { useFxRates } from "@/hooks/useFxRates";
import { useVisitorLocale } from "@/lib/visitor-locale";
import { usePublicPlans } from "@/hooks/usePublicPlans";
import type { BillingCycle } from "@/constants/plans";
import { billingCurrencyFor, resolvePlanPrice, type GhsRates, type PricedPlan } from "@/lib/plan-pricing";

/**
 * Prices for this person, in their own money. `override` (country or
 * currency) wins — sign-up passes the country they picked, since that's
 * what they'll be billed for.
 */
export function usePlanPricing(override?: { country?: string | null; currency?: string | null }) {
  const signedIn = useAuthStore((s) => s.isAuthenticated);
  const businessCode = useCurrencyStore((s) => s.code);
  // Signed out: where they are, or the country they picked (its rate too).
  const visitor = useVisitorLocale(override?.country || undefined, !signedIn);
  const { chargeable, plans } = usePublicPlans();
  const fromCountry = override?.country ? COUNTRIES.find((c) => c.code === override.country)?.currency : null;
  const local = String(override?.currency || fromCountry || (signedIn ? businessCode : visitor.data?.currency) || "GHS").toUpperCase();
  // Rates (GHS base): the signed-in feed, else the visitor's own rate.
  const fx = useFxRates("GHS", signedIn && local !== "GHS");
  const visitorRate = visitor.data?.currency === local ? visitor.data?.planRate ?? null : null;
  const rates: GhsRates = { ...(fx.data?.rates || {}), ...(local !== "GHS" && !fx.data?.rates?.[local] && visitorRate ? { [local]: visitorRate } : {}) };
  return {
    local,
    billing: billingCurrencyFor(local, chargeable),
    chargeable,
    plans,
    rates,
    price: (plan: PricedPlan, cycle: BillingCycle) => resolvePlanPrice(plan, cycle, local, chargeable, rates),
  };
}
